/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/activities-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-activities-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/activities-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, activity_type_id, stage_id, status_id, is_blocking, trig_area_lot_id, parent_activity_id, area_id, trig_area_stage_id, needs_follow_up, trig_organization_id, photo_required, trig_effective_photo_required
 * - Sortable fields: created_at, updated_at
 * - Required fields: none
 * - Readonly fields: id, created_at, updated_at, trig_area_lot_id, trig_activity_type_name, trig_activity_type_color, trig_activity_status_name, trig_activity_status_color, trig_activity_stage_name, trig_area_stage_id, trig_lot_address, trig_lot_number, trig_builder_name, trig_lot_phase_name, trig_job_name, city, trig_category_name, trig_organization_id, trig_assigned_users, trig_effective_photo_required
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/activities-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-activities-api-client.ts (API client)
 * - client/api/generated_schemas/activities.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-09-19T18:30:21.782Z
 * Source config: config/entities/activities-entity-config.ts
 * Public fields: 49/49 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ActivitiesClient, ActivitiesQueryOptions } from '../generated_clients/generated-activities-api-client.js';
import {
  Activities,
  ActivitiesInsert,
  ActivitiesUpdate,
  ActivitiesFieldConfig
} from '../generated_schemas/generated-activities-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/activities_business_hooks/

// Query keys with enhanced caching strategy
export const activitiesKeys = {
  all: ['activities'] as const,
  lists: () => [...activitiesKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...activitiesKeys.lists(), { filters }] as const,
  details: () => [...activitiesKeys.all, 'detail'] as const,
  detail: (id: string) => [...activitiesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...activitiesKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...activitiesKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...activitiesKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...activitiesKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useActivitiesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new ActivitiesClient();
};

/**
 * Hook to get all activities with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with activities data
 */
export const useActivitiesList = (options?: ActivitiesQueryOptions) => {
  const client = useActivitiesClient();

  return useQuery({
    queryKey: activitiesKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
    // Always reconcile with the server on mount, even when persisted/cached
    // data is restored (field PWA offline cache) or a cross-device write
    // happened elsewhere. Cached data still paints instantly; the refetch
    // corrects it. Offline, the refetch simply fails and the cache is kept.
    // Nobody should ever see stale operational data. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * Hook to get a single activitie by ID
 *
 * @param id - The activitie ID
 * @returns React Query result with single activitie data
 */
export const useActivities = (id: string) => {
  const client = useActivitiesClient();

  return useQuery({
    queryKey: activitiesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
    // Always reconcile with the server on mount so a reload / navigation never
    // paints a stale restored (persisted) detail snapshot. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/activities
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/activities_business_hooks/
 */

/**
 * Hook to create a new activitie
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: none
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at, trig_area_lot_id, trig_activity_type_name, trig_activity_type_color, trig_activity_status_name, trig_activity_status_color, trig_activity_stage_name, trig_area_stage_id, trig_lot_address, trig_lot_number, trig_builder_name, trig_lot_phase_name, trig_job_name, city, trig_category_name, trig_organization_id, trig_assigned_users, trig_effective_photo_required
 *
 * @returns React Query mutation for creating activitie
 */
export const useCreateActivities = () => {
  const client = useActivitiesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: ActivitiesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activitiesKeys.all });
    },
  });
};

/**
 * Hook to update an existing activitie
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at, trig_area_lot_id, trig_activity_type_name, trig_activity_type_color, trig_activity_status_name, trig_activity_status_color, trig_activity_stage_name, trig_area_stage_id, trig_lot_address, trig_lot_number, trig_builder_name, trig_lot_phase_name, trig_job_name, city, trig_category_name, trig_organization_id, trig_assigned_users, trig_effective_photo_required
 *
 * @returns React Query mutation for updating activitie
 */
export const useUpdateActivities = () => {
  const client = useActivitiesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ActivitiesUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: activitiesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: activitiesKeys.lists() });
    },
  });
};

/**
 * Hook to delete a activitie
 *
 * @returns React Query mutation for deleting activitie
 */
export const useDeleteActivities = () => {
  const client = useActivitiesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activitiesKeys.all });
    },
  });
};

/**
 * Hook to count activities with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, activity_type_id, stage_id, status_id, is_blocking, trig_area_lot_id, parent_activity_id, area_id, trig_area_stage_id, needs_follow_up, trig_organization_id, photo_required, trig_effective_photo_required
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useActivitiesCount = (filter?: Partial<Record<typeof ActivitiesFieldConfig.filterable[number], any>>) => {
  const client = useActivitiesClient();
  
  return useQuery({
    queryKey: activitiesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useActivitiesFieldConfig = () => {
  return ActivitiesFieldConfig;
};